import { exampleMenu } from "./exampleMenu";
// Leitura (não limpeza) do cache de segmento. Importa a constante em vez de
// repetir o literal: INV-F2 exige que "nx_tenant_segment" exista num lugar só,
// senão um rename futuro deixa esta leitura apontando para chave morta.
import { TENANT_SEGMENT_STORAGE_KEY } from "../../auth/session";

/**
 * buildMenu — fonte única do menu lateral para Agenda e Visão geral.
 *
 * Centraliza (sem alterar comportamento) duas responsabilidades que antes
 * viviam duplicadas:
 *
 *  1. FILTRO por `requiresSegment` — esconde itens que não casam com o
 *     segmento do tenant (ex.: "Fidelidade" só para barbearias). Vale agora
 *     nas DUAS telas, para consistência. Usa o mesmo fallback de cache de
 *     segmento que o Agenda.js usava (localStorage "nx_tenant_segment"),
 *     evitando o "piscar" do item antes de `businessData` chegar.
 *
 *  2. INJEÇÃO de handlers/navegação:
 *     - variant "agenda"     → onClick reais das ações (WhatsApp/Instagram,
 *       Conversas, Serviços, Relatórios, children de Profissionais).
 *     - variant "visaoGeral" → itens de AÇÃO (sem `link`) → navigate("/agenda"),
 *       onde a ação real é acionada (decisão A). Itens com `link` navegam normal.
 *
 *  Em AMBOS os variants o card `empresa` recebe:
 *     - label = nome real do tenant (fallback "Minha empresa")
 *     - onClick → navigate("/business/settings")
 *
 * @param {object}   params
 * @param {object}   [params.businessData]  resposta de GET /tenants/me
 * @param {Function} params.navigate        useNavigate()
 * @param {object}   [params.handlers]      { handleConnectWhatsApp,
 *                                            handleConnectWhatsAppCoexistence,
 *                                            handleConnectInstagram }
 * @param {"agenda"|"visaoGeral"} params.variant
 * @param {string|null} [params.role]        papel do usuário no tenant
 *                                            (OWNER | ADMIN | MEMBER) — usado
 *                                            para esconder itens `requiresRole`.
 * @param {boolean} [params.crmIncluded]      plano inclui a Central de
 *                                            Atendimento (CRM) — usado para
 *                                            esconder itens `requiresCrm`.
 * @param {boolean} [params.schedulingIncluded] plano inclui agendamento
 *                                            (ADR-0026). Itens
 *                                            `requiresScheduling` NÃO somem
 *                                            quando é false — ganham
 *                                            `locked: true`. Default `true`
 *                                            (fail-safe, ver abaixo).
 * @returns {Array} itens prontos para <Sidebar menuItems>
 */
export function buildMenu({
  businessData,
  navigate,
  handlers = {},
  variant = "agenda",
  role = null,
  crmIncluded = false,
  // ⚠️ Default `true`, ao contrário de `crmIncluded` (que nega por omissão).
  // Agendamento existe em todos os planos menos um; sumir com o menu inteiro da
  // agenda porque o campo não chegou seria o pior erro possível. Mesmo
  // fail-safe de `usePlanFeature("scheduling")`.
  schedulingIncluded = true,
}) {
  // ── Segmento atual (com fallback de cache, igual ao Agenda.js) ──────────
  const cachedSegment = (() => {
    try {
      return window.localStorage.getItem(TENANT_SEGMENT_STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  const currentSegment =
    businessData?.tenant?.segment ?? cachedSegment ?? null;

  const isItemAllowedForSegment = (item) =>
    !item.requiresSegment || item.requiresSegment === currentSegment;

  // Gating por papel: itens com `requiresRole` (array) só aparecem quando o
  // papel atual está na lista. Sem papel conhecido (null, ex.: ainda
  // carregando), o item permanece escondido — padrão seguro (nega por omissão).
  const isItemAllowedForRole = (item) =>
    !item.requiresRole || (!!role && item.requiresRole.includes(role));

  // Gating por plano: itens `requiresCrm` (ex.: "Setores") só aparecem quando o
  // plano do tenant inclui a Central de Atendimento (`crmIncluded`). Nega por
  // omissão — o Básico não vê nada de CRM no menu.
  const isItemAllowedForCrm = (item) => !item.requiresCrm || !!crmIncluded;

  // Itens `requiresNoScheduling` são o INVERSO: só existem para quem NÃO tem
  // agendamento (ex.: atalho "Perguntas frequentes", o onboarding do Plano
  // Atendimento). Não é gate de capacidade — é adaptação do menu ao tier.
  const isItemAllowedForNoScheduling = (item) =>
    !item.requiresNoScheduling || schedulingIncluded === false;

  const isItemAllowed = (item) =>
    isItemAllowedForSegment(item) &&
    isItemAllowedForRole(item) &&
    isItemAllowedForCrm(item) &&
    isItemAllowedForNoScheduling(item);

  // Gating por capacidade de agendamento: itens `requiresScheduling` NÃO são
  // filtrados — recebem `locked: true` e continuam no menu com cadeado. ADR-0026
  // §Decisão: "UI bloqueada e não escondida, porque esconder mata a descoberta,
  // o único motor de upgrade do tier". Só `false` explícito tranca (fail-safe).
  const markLock = (item) =>
    item.requiresScheduling && schedulingIncluded === false
      ? { ...item, locked: true }
      : item;

  // ── 1. Filtro por segmento + papel (item + seus children) ───────────────
  const filteredMenu = exampleMenu
    .filter(isItemAllowed)
    .map(markLock)
    .map((item) =>
      item.children
        ? {
            ...item,
            children: item.children.filter(isItemAllowed).map(markLock),
          }
        : item,
    );

  const companyLabel = businessData?.tenant?.name || "Minha empresa";

  // ── 2. Injeção de comportamento por item ────────────────────────────────
  const wireAgenda = (item) => {
    switch (item.key) {
      case "empresa":
        return {
          ...item,
          label: companyLabel,
          onClick: () => navigate("/business/settings"),
        };
      case "whatsApp":
        return { ...item, onClick: handlers.handleConnectWhatsApp };
      case "whatsAppCoexistence":
        return {
          ...item,
          onClick: handlers.handleConnectWhatsAppCoexistence,
        };
      case "instagram":
        return { ...item, onClick: handlers.handleConnectInstagram };
      case "services":
        return { ...item, onClick: () => navigate("/business/services") };
      case "messages-dock":
        return {
          ...item,
          onClick: () => navigate("/business/messages-center"),
        };
      case "reports":
        return { ...item, onClick: () => navigate("/business/reports") };
      case "professionals":
        return {
          ...item,
          children: item.children?.map((child) => {
            if (child.key === "list")
              return {
                ...child,
                onClick: () => navigate("/business/professionals"),
              };
            if (child.key === "business_hours")
              return {
                ...child,
                onClick: () => navigate("/business/professionals/hours"),
              };
            return child;
          }),
        };
      default:
        return item;
    }
  };

  // Visão geral: ações sem `link` redirecionam para /agenda; o card "empresa"
  // mantém o destino real (config da empresa).
  const ACTION_KEYS = new Set([
    "whatsApp",
    "whatsAppCoexistence",
    "instagram",
    "messages-dock",
    "services",
    "reports",
  ]);

  const wireVisaoGeral = (item) => {
    if (item.key === "empresa") {
      return {
        ...item,
        label: companyLabel,
        onClick: () => navigate("/business/settings"),
      };
    }
    if (ACTION_KEYS.has(item.key)) {
      return { ...item, onClick: () => navigate("/agenda") };
    }
    if (item.children) {
      return { ...item, children: item.children.map(wireVisaoGeral) };
    }
    return item;
  };

  const wire = variant === "visaoGeral" ? wireVisaoGeral : wireAgenda;
  return filteredMenu.map(wire);
}

/**
 * flattenMenuLinks — achata o menu (itens + children) numa lista
 * `{ key, link }` apenas para itens navegáveis por `link`.
 * Itens de ação (só `onClick`, ex.: conectar WhatsApp) não têm `link`
 * e portanto não participam do highlight por rota.
 */
export function flattenMenuLinks(items = []) {
  const acc = [];
  const walk = (list) => {
    for (const it of list) {
      if (it && it.link) acc.push({ key: it.key, link: it.link });
      if (it && it.children) walk(it.children);
    }
  };
  walk(Array.isArray(items) ? items : []);
  return acc;
}

/**
 * deriveActiveKey — descobre qual item do menu está ativo a partir do
 * `pathname` atual (React Router `useLocation`). Casa por PREFIXO e escolhe
 * o `link` mais LONGO que bate, para que sub-rotas (ex.:
 * `/business/professionals/new`) marquem o item mais específico, não o pai.
 *
 * @param {string} pathname   location.pathname atual
 * @param {Array}  items      menu já construído (buildMenu) ou exampleMenu
 * @returns {string|null}     `key` do item ativo, ou null se nenhum casar
 */
export function deriveActiveKey(pathname, items = []) {
  if (!pathname) return null;
  const links = flattenMenuLinks(items);
  let best = null;
  for (const { key, link } of links) {
    if (!link) continue;
    // Match exato OU o link é prefixo de um segmento (evita casar
    // "/business/reports" com "/business/reports-x").
    const isMatch = pathname === link || pathname.startsWith(`${link}/`);
    if (isMatch && (!best || link.length > best.link.length)) {
      best = { key, link };
    }
  }
  return best ? best.key : null;
}

/**
 * deriveParentKeys — dado o `activeKey` e o menu, devolve as `key`s dos itens
 * PAI cujos `children` contêm o item ativo. Usado para auto-abrir o grupo do
 * item ativo ao montar a sidebar (senão um item aninhado ativo fica invisível
 * até o usuário expandir o grupo). Itens top-level (sem pai) devolvem [].
 *
 * @param {string|null} activeKey  key do item ativo (deriveActiveKey)
 * @param {Array}       items      menu construído (buildMenu) ou exampleMenu
 * @returns {string[]}             keys dos grupos a abrir (0 ou mais)
 */
export function deriveParentKeys(activeKey, items = []) {
  if (!activeKey || !Array.isArray(items)) return [];
  const parents = [];
  for (const item of items) {
    if (item?.children?.some((child) => child.key === activeKey)) {
      parents.push(item.key);
    }
  }
  return parents;
}
